import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "./lib/api";
import InfoTip from "./InfoTip";
import TrendChart from "./TrendChart";
import type { Site } from "./types";

const METRIC_HELP = {
  clicks: "Quante volte qualcuno ha cliccato il sito nei risultati di Google.",
  impressions: "Quante volte Google ha mostrato il sito, anche senza click.",
  ctr: "Su 100 persone che hanno visto il sito su Google, quante ci hanno cliccato.",
  position: "Posto medio in classifica. 1 è il primo risultato.",
  page: "Quale pagina del tuo sito è apparsa per quella ricerca.",
  query: "Cosa ha scritto la persona nella barra di Google.",
} as const;

function MetricLabel({
  name,
  hint,
}: {
  name: string;
  hint: keyof typeof METRIC_HELP;
}) {
  return (
    <span className="metric-label">
      {name}
      <InfoTip label={name} text={METRIC_HELP[hint]} />
    </span>
  );
}

const METRIC_STYLE = {
  clicks: { color: "#4f46e5", label: "Click organici" },
  impressions: { color: "#0d9488", label: "Impressioni" },
  position: { color: "#d97706", label: "Posizione media" },
} as const;

type RangeDays = 7 | 28 | 90 | 180 | 365;

interface OverviewUnlocked {
  unlocked: true;
  message?: string;
  provider: {
    searchConsole: "connected" | "not_connected";
    propertyId?: string;
    note?: string;
  };
  range?: {
    days: number;
    startDate: string;
    endDate: string;
  };
  kpis?: {
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
    clicksChange: number | null;
    impressionsChange: number | null;
    ctrChange: number | null;
    positionChange: number | null;
    organicLeads: number | null;
    organicLeadsNote: string;
  };
  chart?: Array<{ date: string; clicks: number; impressions: number; position: number; ctr: number }>;
  keywordDistribution?: { top3: number; top10: number; top20: number; top50: number; top100: number; other: number };
  queries?: Array<{
    query: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
    clickDelta: number;
    positionDelta: number | null;
  }>;
  topGrowingQueries?: Array<{ query: string; clicks: number; clickDelta: number; position: number }>;
  topDecliningQueries?: Array<{ query: string; clicks: number; clickDelta: number; position: number }>;
  topGrowingPages?: Array<{ page: string; clicks: number; clickDelta: number; position: number }>;
  contentPerformance?: {
    rule: string;
    articlesWithImpressions: number;
    articlesWithClicks: number;
    totals: { clicks: number; impressions: number; ctr: number; position: number };
    clicksChange: number | null;
    impressionsChange: number | null;
    articles: Array<{
      page: string;
      clicks: number;
      impressions: number;
      ctr: number;
      position: number;
      clickDelta: number;
      flags: { growing: boolean; losing: boolean; lowCtrHighImpressions: boolean; nearPageOne: boolean };
    }>;
  };
  opportunities?: Array<{
    type: string;
    title: string;
    detail: string;
    evidence: Record<string, number | string | null>;
  }>;
  universeSeoScore?: {
    score: number | null;
    partial: boolean;
    previousScore: number | null;
    change: number | null;
    components: { visibility?: number; content?: number; rankings?: number };
    label: string;
  };
  placeholders?: Record<string, string>;
}

interface OverviewLocked {
  unlocked: false;
  message: string;
}

type Overview = OverviewUnlocked | OverviewLocked;

type ChartMetric = "clicks" | "impressions" | "position";
type QueryHit = {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};
type QueryLookup = {
  query: string;
  exact: boolean;
  found: boolean;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};
type PageHit = {
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};
type Benchmark = {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  clicksChange: number | null;
  impressionsChange: number | null;
  ctrChange: number | null;
  positionChange: number | null;
  chart: Array<{ date: string; clicks: number; impressions: number; position: number }>;
};
type QueryBand = "all" | "page1" | "page2" | "beyond";

function positionBand(position: number): { label: string; tone: string } {
  if (position <= 10) return { label: "Prima pagina", tone: "" };
  if (position <= 20) return { label: "Seconda pagina", tone: "mid" };
  return { label: "Oltre", tone: "far" };
}
const integer = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 });
const number = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 1 });
const percent = new Intl.NumberFormat("it-IT", { style: "percent", maximumFractionDigits: 1 });
const dateShort = new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "short" });

function fmtDate(value: string) {
  return dateShort.format(new Date(`${value}T12:00:00Z`));
}

function Change({ value, invert = false }: { value: number | null | undefined; invert?: boolean }) {
  if (value == null || Number.isNaN(value)) return <span className="seo-intel-change neutral">Nessun confronto</span>;
  const positive = invert ? value <= 0 : value >= 0;
  if (invert) {
    return (
      <span className={`seo-intel-change ${positive ? "positive" : "negative"}`}>
        {value <= 0 ? "↑ sale di" : "↓ scende di"} {number.format(Math.abs(value))} pos.
      </span>
    );
  }
  return (
    <span className={`seo-intel-change ${positive ? "positive" : "negative"}`}>
      {value >= 0 ? "↑" : "↓"} {number.format(Math.abs(value))}%
    </span>
  );
}

function shortPath(value: string) {
  try {
    const url = new URL(value);
    return url.pathname + url.search;
  } catch {
    return value.replace(/^https?:\/\/[^/]+/i, "") || value;
  }
}

function benchmarkFrom(rows: QueryHit[]): Benchmark {
  const impressions = rows.reduce((sum, row) => sum + row.impressions, 0);
  const clicks = rows.reduce((sum, row) => sum + row.clicks, 0);
  return {
    clicks,
    impressions,
    ctr: impressions ? clicks / impressions : 0,
    position: impressions ? rows.reduce((sum, row) => sum + row.position * row.impressions, 0) / impressions : 0,
    clicksChange: null,
    impressionsChange: null,
    ctrChange: null,
    positionChange: null,
    chart: [],
  };
}

export default function SeoIntelligencePanel({
  site,
  unlocked,
}: {
  site: Site;
  unlocked: boolean;
}) {
  const [range, setRange] = useState<RangeDays>(28);
  const [metric, setMetric] = useState<ChartMetric>("clicks");
  const [queryText, setQueryText] = useState("");
  const [searched, setSearched] = useState("");
  const [searchHits, setSearchHits] = useState<QueryHit[] | null>(null);
  const [searchPages, setSearchPages] = useState<PageHit[]>([]);
  const [benchmark, setBenchmark] = useState<Benchmark | null>(null);
  const [lookup, setLookup] = useState<QueryLookup | null>(null);
  const [watched, setWatched] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [band, setBand] = useState<QueryBand>("all");
  const [showAll, setShowAll] = useState(false);
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const finderRef = useRef<HTMLElement>(null);
  const searchSeq = useRef(0);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(`seo-watch:${site.id}`) || "[]");
      if (Array.isArray(stored)) {
        setWatched(stored.filter((item): item is string => typeof item === "string").slice(0, 12));
      }
    } catch {
      setWatched([]);
    }
  }, [site.id]);
  useEffect(() => {
    if (!unlocked) {
      setData({
        unlocked: false,
        message: "SEO / GEO Intelligence non è attivo su questo progetto. Contatta Universe per sbloccarlo.",
      });
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError("");
    api<Overview>(`/api/sites/${site.id}/seo-intelligence?range=${range}`)
      .then((result) => {
        if (!active) return;
        setData(result);
      })
      .catch((cause) => {
        if (!active) return;
        setError(cause instanceof Error ? cause.message : "SEO Intelligence non disponibile");
        setData(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [range, site.id, unlocked]);

  const visibleQueries = useMemo(() => {
    const source = searchHits ?? (data && "queries" in data ? data.queries : undefined) ?? [];
    if (searchHits) return source;
    return source.filter((row) => {
      if (band === "page1") return row.position <= 10;
      if (band === "page2") return row.position > 10 && row.position <= 20;
      if (band === "beyond") return row.position > 20;
      return true;
    });
  }, [band, data, searchHits]);

  function rememberWatch(query: string) {
    setWatched((current) => {
      const next = [query, ...current.filter((item) => item.toLowerCase() !== query.toLowerCase())].slice(0, 12);
      localStorage.setItem(`seo-watch:${site.id}`, JSON.stringify(next));
      return next;
    });
  }
  function forgetWatch(query: string) {
    setWatched((current) => {
      const next = current.filter((item) => item.toLowerCase() !== query.toLowerCase());
      localStorage.setItem(`seo-watch:${site.id}`, JSON.stringify(next));
      return next;
    });
  }
  function clearSearch() {
    searchSeq.current += 1;
    setQueryText("");
    setSearched("");
    setSearchHits(null);
    setSearchPages([]);
    setBenchmark(null);
    setLookup(null);
    setSearchError("");
    setSearching(false);
  }

  async function runSearch(text = queryText, days: RangeDays = range) {
    const q = text.trim();
    if (q.length < 2) {
      setSearchError("Scrivi almeno due lettere.");
      return;
    }
    const seq = ++searchSeq.current;
    const needles = q.toLowerCase().split(/\s+/).filter(Boolean);
    const loaded = data && "queries" in data ? data.queries || [] : [];
    const local = loaded.filter((row) => needles.every((needle) => row.query.toLowerCase().includes(needle)));
    setSearching(true);
    setSearchError("");
    setSearched(q);
    setBand("all");
    setSearchHits(local);
    setSearchPages([]);
    setBenchmark(local.length ? benchmarkFrom(local) : null);
    const localExact = local.find((row) => row.query.toLowerCase() === q.toLowerCase()) || local[0];
    setLookup(localExact ? {
      query: localExact.query,
      exact: localExact.query.toLowerCase() === q.toLowerCase(),
      found: true,
      clicks: localExact.clicks,
      impressions: localExact.impressions,
      ctr: localExact.ctr,
      position: localExact.position,
    } : { query: q, exact: false, found: false, clicks: 0, impressions: 0, ctr: 0, position: 0 });
    rememberWatch(q);
    try {
      const result = await api<{
        queries: QueryHit[];
        pages?: PageHit[];
        benchmark?: Benchmark;
        lookup?: QueryLookup;
      }>(
        `/api/sites/${site.id}/seo-intelligence/queries?range=${days}&q=${encodeURIComponent(q)}`,
      );
      if (seq !== searchSeq.current) return;
      const queries = result.queries || [];
      setSearchHits(queries.length ? queries : local);
      setSearchPages(result.pages || []);
      setLookup(result.lookup || (queries[0] ? {
        query: queries[0].query,
        exact: queries[0].query.toLowerCase() === q.toLowerCase(),
        found: true,
        clicks: queries[0].clicks,
        impressions: queries[0].impressions,
        ctr: queries[0].ctr,
        position: queries[0].position,
      } : { query: q, exact: false, found: false, clicks: 0, impressions: 0, ctr: 0, position: 0 }));
      setBenchmark(result.benchmark && (result.benchmark.impressions > 0 || queries.length) ? result.benchmark : local.length ? benchmarkFrom(local) : result.benchmark || null);
      window.setTimeout(() => finderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
    } catch (cause) {
      if (seq !== searchSeq.current) return;
      setSearchError(cause instanceof Error ? cause.message : "Ricerca non riuscita");
    } finally {
      if (seq === searchSeq.current) setSearching(false);
    }
  }

  useEffect(() => {
    const q = queryText.trim();
    if (q.length < 2 || q === searched) return;
    const timer = window.setTimeout(() => void runSearch(q), 550);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryText]);

  if (!unlocked || (data && data.unlocked === false)) {
    return (
      <section className="seo-intel-locked">
        <div>
          <p className="eyebrow indigo">Modulo a pagamento</p>
          <h3>SEO / GEO Intelligence</h3>
          <p>
            Dashboard Search Console, Universe SEO Score, opportunità organiche e performance degli articoli
            in <code>/blog/</code>. Si sblocca dal pannello master Universe dopo l&apos;attivazione.
          </p>
        </div>
        <aside>
          <strong>Non attivo</strong>
          <small>{data && data.unlocked === false ? data.message : "Contatta Universe per attivarlo su questo sito."}</small>
        </aside>
      </section>
    );
  }

  if (loading && !data) {
    return <div className="seo-intel-loading"><i /><i /><i /><i /></div>;
  }

  if (error) {
    return <div className="notice error">{error}</div>;
  }

  if (!data || data.unlocked !== true) return null;

  if (data.provider.searchConsole !== "connected" || !data.kpis) {
    return (
      <section className="seo-intel-panel">
        <header className="seo-intel-hero">
          <div>
            <p className="eyebrow">SEO / GEO Intelligence</p>
            <h3>Collega Search Console</h3>
            <p>{data.message || "Senza Google Search Console non mostriamo dati inventati. Collega l’integrazione nelle impostazioni del sito."}</p>
          </div>
          <div className="seo-intel-range" aria-label="Periodo">
            <small>Periodo</small>
            <div>
              {([7, 28, 90, 180, 365] as RangeDays[]).map((days) => (
                <button
                  key={days}
                  type="button"
                  className={range === days ? "active" : ""}
                  onClick={() => setRange(days)}
                >
                  {days === 365 ? "1 anno" : `${days} gg`}
                </button>
              ))}
            </div>
          </div>
        </header>
      </section>
    );
  }

  const score = data.universeSeoScore;
  const content = data.contentPerformance;
  const chart = data.chart ?? [];

  return (
    <section className="seo-intel-panel">
      <header className="seo-intel-hero">
        <div>
          <p className="eyebrow">SEO / GEO Intelligence</p>
          <h3>Visibilità organica reale</h3>
          <p>
            Dati Search Console, score interno Universe e lettura blog su URL <code>/blog/</code>.
            Nessun numero demo: se una fonte manca, lo score resta parziale.
          </p>
        </div>
        <div className="seo-intel-range" aria-label="Periodo">
          <small>Periodo</small>
          <div>
            {([7, 28, 90, 180, 365] as RangeDays[]).map((days) => (
              <button
                key={days}
                type="button"
                className={range === days ? "active" : ""}
                disabled={loading}
                onClick={() => {
                  setRange(days);
                  if (searched) void runSearch(searched, days);
                }}
              >
                {days === 365 ? "1 anno" : `${days} gg`}
              </button>
            ))}
          </div>
          {data.range && (
            <span>
              {fmtDate(data.range.startDate)} - {fmtDate(data.range.endDate)}
            </span>
          )}
        </div>
      </header>

      <div className="seo-intel-score-row">
        <article className="seo-intel-score">
          <small>{score?.label || "Universe SEO Score"}</small>
          <strong>{score?.score == null ? "—" : score.score}</strong>
          <Change value={score?.change ?? null} />
          {score?.partial && <em>Parziale: mancano fonti tecniche o conversioni</em>}
          <ul>
            <li><span>Visibility</span><b>{score?.components.visibility != null ? Math.round(score.components.visibility) : "—"}</b></li>
            <li><span>Content</span><b>{score?.components.content != null ? Math.round(score.components.content) : "—"}</b></li>
            <li><span>Rankings</span><b>{score?.components.rankings != null ? Math.round(score.components.rankings) : "—"}</b></li>
          </ul>
        </article>
        <div className="seo-intel-kpis">
          <article>
            <small><MetricLabel name="Click" hint="clicks" /></small>
            <strong>{integer.format(data.kpis.clicks)}</strong>
            <Change value={data.kpis.clicksChange} />
          </article>
          <article>
            <small><MetricLabel name="Impressioni" hint="impressions" /></small>
            <strong>{integer.format(data.kpis.impressions)}</strong>
            <Change value={data.kpis.impressionsChange} />
          </article>
          <article>
            <small><MetricLabel name="CTR" hint="ctr" /></small>
            <strong>{percent.format(data.kpis.ctr)}</strong>
            <Change value={data.kpis.ctrChange} />
          </article>
          <article>
            <small><MetricLabel name="Posizione media" hint="position" /></small>
            <strong>{number.format(data.kpis.position)}</strong>
            <Change value={data.kpis.positionChange} invert />
          </article>
        </div>
      </div>

      {chart.length > 0 && (
        <section className="seo-intel-chart">
          <header>
            <div>
              <p className="eyebrow">Andamento</p>
              <h4>Click, impressioni e posizione</h4>
              <p className="seo-intel-chart-hint">
                Tre letture distinte, non un solo grafico. Tocca una carta per ingrandirla.
              </p>
            </div>
          </header>
          <div className="seo-intel-chart-grid">
            {([
              ["clicks", false],
              ["impressions", false],
              ["position", true],
            ] as const).map(([key, invert]) => (
              <article
                key={key}
                className={metric === key ? "active" : ""}
                style={{ "--metric": METRIC_STYLE[key].color } as React.CSSProperties}
              >
                <button type="button" onClick={() => setMetric(key)}>
                  <small>{METRIC_STYLE[key].label}</small>
                  <strong>
                    {key === "position"
                      ? number.format(data.kpis?.position || 0)
                      : integer.format(Math.round(data.kpis?.[key] || 0))}
                  </strong>
                </button>
                <TrendChart
                  compact={metric !== key}
                  label={METRIC_STYLE[key].label}
                  color={METRIC_STYLE[key].color}
                  invert={invert}
                  format={(value) =>
                    key === "position" ? number.format(value) : integer.format(Math.round(value))
                  }
                  points={chart
                    .filter((row) => key !== "position" || row.impressions > 0)
                    .map((row) => ({ date: row.date, value: row[key] }))}
                />
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="seo-intel-finder" ref={finderRef}>
        <header>
          <h4>Dove compare il sito</h4>
          <p>
            Cerca una parola che interessa al cliente. La posizione è quella media di Google nel periodo, sulle ricerche che hanno avuto almeno un’impressione.
          </p>
        </header>
        {data.keywordDistribution && (
          <div className="seo-intel-bands" aria-label="Distribuzione posizioni">
            <button type="button" className={band === "all" && !searched ? "active" : ""} onClick={() => { clearSearch(); setBand("all"); }}>
              Tutte {data.queries?.length || 0}
            </button>
            <button type="button" className={band === "page1" && !searched ? "active" : ""} onClick={() => { clearSearch(); setBand("page1"); }}>
              Prima pagina {data.keywordDistribution.top3 + data.keywordDistribution.top10}
            </button>
            <button type="button" className={band === "page2" && !searched ? "active" : ""} onClick={() => { clearSearch(); setBand("page2"); }}>
              Seconda pagina {data.keywordDistribution.top20}
            </button>
            <button type="button" className={band === "beyond" && !searched ? "active" : ""} onClick={() => { clearSearch(); setBand("beyond"); }}>
              Oltre {data.keywordDistribution.top50 + data.keywordDistribution.top100 + data.keywordDistribution.other}
            </button>
          </div>
        )}
        <form
          className="seo-intel-search"
          onSubmit={(event) => {
            event.preventDefault();
            void runSearch();
          }}
        >
          <span className="eyebrow">Cerca una parola</span>
          <div className="seo-intel-search-field">
            <input
              value={queryText}
              onChange={(event) => setQueryText(event.target.value)}
              placeholder="Es. nome attività, città, servizio"
              type="text"
              enterKeyHint="search"
              autoComplete="off"
              aria-label="Parola da cercare su Google"
            />
            {queryText && (
              <button type="button" className="seo-intel-clear" aria-label="Cancella ricerca" onClick={clearSearch}>
                ×
              </button>
            )}
          </div>
          <button type="submit" disabled={searching || queryText.trim().length < 2}>
            {searching ? "Cerco…" : "Cerca"}
          </button>
        </form>
        {watched.length > 0 && (
          <div className="seo-intel-watch" aria-label="Parole cercate di recente">
            {watched.map((item) => (
              <button
                key={item}
                type="button"
                className={searched.toLowerCase() === item.toLowerCase() ? "active" : ""}
                onClick={() => {
                  setQueryText(item);
                  void runSearch(item);
                }}
              >
                {item}
              </button>
            ))}
          </div>
        )}
        {searchError && <p className="notice error">{searchError}</p>}
        {searched && lookup && (() => {
          const exact = lookup.exact ? lookup : searchHits?.find((row) => row.query.toLowerCase() === searched.toLowerCase());
          const shown = lookup.found ? (exact?.position ?? lookup.position) : 0;
          const place = lookup.found ? positionBand(shown) : { label: "Non ancora visibile", tone: "far" };
          return (
          <div className={`seo-intel-benchmark${searching ? " is-loading" : ""}`} aria-live="polite">
            <div className={`seo-intel-result-card${lookup.found ? "" : " empty"}`}>
              <div>
                <small>
                  {lookup.found
                    ? lookup.exact
                      ? `Posizione su Google per «${lookup.query}»`
                      : `Ricerche vicine a «${searched}»`
                    : `Nessuna impressione per «${searched}»`}
                </small>
                <strong>{lookup.found ? number.format(shown) : "—"}</strong>
                <em className={place.tone}>{place.label}</em>
              </div>
              <p>
                {searching
                  ? "Aggiorno con gli ultimi dati di Search Console…"
                  : lookup.found
                    ? lookup.exact
                      ? `Dato medio del periodo, calcolato su ${integer.format(lookup.impressions)} impressioni.`
                      : `Il sito compare per ricerche simili. La più vicina è «${lookup.query}».`
                    : "In questo periodo Google non ha mostrato il sito per questa parola. Resta nella lista qui sopra: al prossimo controllo vedrai se entra in classifica."}
              </p>
              <button type="button" className="seo-intel-forget" onClick={() => forgetWatch(searched)}>
                Togli dalla lista
              </button>
            </div>
            <div className="seo-intel-kpis">
              <article>
                <small><MetricLabel name="Click" hint="clicks" /></small>
                <strong>{integer.format(benchmark?.clicks ?? lookup.clicks)}</strong>
                <Change value={benchmark?.clicksChange} />
              </article>
              <article>
                <small><MetricLabel name="Impressioni" hint="impressions" /></small>
                <strong>{integer.format(benchmark?.impressions ?? lookup.impressions)}</strong>
                <Change value={benchmark?.impressionsChange} />
              </article>
              <article>
                <small><MetricLabel name="CTR" hint="ctr" /></small>
                <strong>{percent.format(benchmark?.ctr ?? lookup.ctr)}</strong>
                <Change value={benchmark?.ctrChange} />
              </article>
              <article>
                <small><MetricLabel name="Posizione media" hint="position" /></small>
                <strong>{lookup.found ? number.format(benchmark?.position ?? lookup.position) : "—"}</strong>
                <Change value={benchmark?.positionChange} invert />
              </article>
            </div>
            {benchmark && benchmark.chart.filter((row) => row.impressions > 0).length > 1 && (
              <div className="seo-intel-benchmark-chart">
                <p>Come cambia la posizione giorno per giorno</p>
                <TrendChart
                  compact
                  label={`Posizione di ${searched}`}
                  color={METRIC_STYLE.position.color}
                  invert
                  format={(value) => number.format(value)}
                  points={benchmark.chart
                    .filter((row) => row.impressions > 0)
                    .map((row) => ({ date: row.date, value: row.position }))}
                />
              </div>
            )}
          </div>
          );
        })()}
        {searched && searching && !lookup && (
          <p className="seo-intel-result-empty">Cerco «{searched}» su Search Console…</p>
        )}
        {searchPages.length > 0 && (
          <div className="seo-intel-query-table">
            <div className="head">
              <span><MetricLabel name="Pagina" hint="page" /></span>
              <span><MetricLabel name="Posizione" hint="position" /></span>
              <span><MetricLabel name="Click" hint="clicks" /></span>
              <span><MetricLabel name="Impressioni" hint="impressions" /></span>
              <span><MetricLabel name="CTR" hint="ctr" /></span>
            </div>
            {searchPages.map((row) => (
              <div key={row.page}>
                <strong title={row.page}>{shortPath(row.page)}</strong>
                <span className="seo-intel-pos">
                  {number.format(row.position)}
                  <em className={positionBand(row.position).tone}>{positionBand(row.position).label}</em>
                </span>
                <span>{integer.format(row.clicks)}</span>
                <span>{integer.format(row.impressions)}</span>
                <span>{percent.format(row.ctr)}</span>
              </div>
            ))}
          </div>
        )}
        {(visibleQueries.length > 0 || !searched) && <div className="seo-intel-query-table">
          <div className="head">
            <span><MetricLabel name="Parola" hint="query" /></span>
            <span><MetricLabel name="Posizione" hint="position" /></span>
            <span><MetricLabel name="Click" hint="clicks" /></span>
            <span><MetricLabel name="Impressioni" hint="impressions" /></span>
            <span><MetricLabel name="CTR" hint="ctr" /></span>
          </div>
          {visibleQueries.slice(0, searched ? 50 : showAll ? 500 : 12).map((row) => {
            const place = positionBand(row.position);
            return (
              <div key={row.query}>
                <strong title={row.query}>{row.query}</strong>
                <span className="seo-intel-pos">
                  {number.format(row.position)}
                  <em className={place.tone}>{place.label}</em>
                </span>
                <span>{integer.format(row.clicks)}</span>
                <span>{integer.format(row.impressions)}</span>
                <span>{percent.format(row.ctr)}</span>
              </div>
            );
          })}
          {!visibleQueries.length && !searched && (
            <p className="seo-intel-result-empty">Nessuna ricerca in questa fascia.</p>
          )}
        </div>}
        {!searched && visibleQueries.length > 12 && (
          <button type="button" className="seo-intel-more" onClick={() => setShowAll((value) => !value)}>
            {showAll ? "Mostra meno" : `Mostra tutte le ${visibleQueries.length} parole`}
          </button>
        )}
      </section>

      <div className="seo-intel-grid">
        <section>
          <header>
            <h4>Opportunità</h4>
            <span>{data.opportunities?.length || 0}</span>
          </header>
          {data.opportunities?.length ? (
            <ul className="seo-intel-opps">
              {data.opportunities.map((item, index) => (
                <li key={`${item.type}-${index}`}>
                  <em>{item.type}</em>
                  <strong>{item.title}</strong>
                  <p>{item.detail}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="seo-intel-empty">Nessuna opportunità evidente in questo periodo.</p>
          )}
        </section>

        <section>
          <header>
            <h4>Content Performance</h4>
            <span>/blog/</span>
          </header>
          {content ? (
            <>
              <div className="seo-intel-blog-kpis">
                <article><small>Articoli con impression</small><strong>{content.articlesWithImpressions}</strong></article>
                <article><small>Articoli con click</small><strong>{content.articlesWithClicks}</strong></article>
                <article><small>Click blog</small><strong>{integer.format(content.totals.clicks)}</strong><Change value={content.clicksChange} /></article>
                <article><small>Impression blog</small><strong>{integer.format(content.totals.impressions)}</strong><Change value={content.impressionsChange} /></article>
              </div>
              <div className="seo-intel-table">
                {content.articles.slice(0, 8).map((article) => (
                  <div key={String(article.page)}>
                    <strong title={String(article.page)}>{shortPath(String(article.page))}</strong>
                    <span>{integer.format(article.clicks)} click</span>
                    <em className={article.clickDelta >= 0 ? "positive" : "negative"}>
                      {article.clickDelta >= 0 ? "+" : ""}
                      {integer.format(article.clickDelta)}
                    </em>
                  </div>
                ))}
                {!content.articles.length && <p className="seo-intel-empty">Nessun URL /blog/ con traffico GSC in questo periodo.</p>}
              </div>
            </>
          ) : (
            <p className="seo-intel-empty">Nessun dato blog disponibile.</p>
          )}
        </section>
      </div>

      <div className="seo-intel-grid seo-intel-grid--queries">
        <section>
          <header><h4>Query in crescita</h4></header>
          <div className="seo-intel-table">
            {(data.topGrowingQueries || []).map((row) => (
              <div key={String(row.query)}>
                <strong>{row.query}</strong>
                <span>{integer.format(row.clicks)} click</span>
                <em>pos. {number.format(row.position)}</em>
              </div>
            ))}
            {!data.topGrowingQueries?.length && <p className="seo-intel-empty">Nessuna query in crescita.</p>}
          </div>
        </section>
        <section>
          <header><h4>Query in calo</h4></header>
          <div className="seo-intel-table">
            {(data.topDecliningQueries || []).map((row) => (
              <div key={String(row.query)}>
                <strong>{row.query}</strong>
                <span>{integer.format(row.clicks)} click</span>
                <em>pos. {number.format(row.position)}</em>
              </div>
            ))}
            {!data.topDecliningQueries?.length && <p className="seo-intel-empty">Nessuna query in calo.</p>}
          </div>
        </section>
      </div>

      {data.placeholders && (
        <section className="seo-intel-placeholders">
          <header>
            <h4>Prossime fonti</h4>
            <p>Visibili solo come stato: niente dati inventati finché non sono collegate.</p>
          </header>
          <div>
            {Object.entries(data.placeholders).map(([key, label]) => (
              <article key={key}>
                <strong>{key}</strong>
                <small>{label}</small>
              </article>
            ))}
          </div>
        </section>
      )}

      {data.provider.note && <p className="seo-intel-note">{data.provider.note}</p>}
    </section>
  );
}
